import {
  CONFIGS,
  getCustomFieldsDefaultValues,
  getCustomFieldsValidationSchema,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { ConfigEntryType } from 'config/config';
import useUserData from 'hooks/useUserData';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { catchErrorMessage, commonRegex } from 'utility/helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { getPredefinedFieldValidation } from '@buildwise/libs/people/helpers/user-form.helpers';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { useSearchItemQuery } from 'hooks/useSearchQuery';
import {
  ProjectsPaginationResponseType,
  ProjectType,
  updateProjectsCache,
} from '@buildwise/pages/projects/hooks/useProjectsQuery';
import { useNavigate } from 'react-router-dom';
import { updateProjectDetailsCache } from '@buildwise/pages/project/hooks/useProjectDetailsQuery';
import { DEFAULT_STANDARD_PROJECT_FIELDS } from '../constants';
import { ProjectsAPI } from '@buildwise/libs/projects';
import { HOME } from 'constants/routes';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';

export type ModifyProjectFormSchema = {
  name: string;
  squareFeet: string;
  address: string;
  city: string;
  state_code: string;
  zip: string;
  campus: string;
  projectRegion: string;
  geography: string;
  description: string;
};

type ProjectsSearchResponseType = ProjectsPaginationResponseType;

export const editProjectSchema = (
  customFields: ConfigEntryType[],
  singularProjectsModuleName: string,
  projects: ProjectsSearchResponseType | undefined,
  project: ProjectType | undefined,
) =>
  Yup.object().shape({
    ...getCustomFieldsValidationSchema({
      fields: customFields,
      getPredefinedFieldValidation: ({ field }) => getPredefinedFieldValidation({ field }),
    }),
    name: Yup.string()
      .required(`${singularProjectsModuleName} name is required`)
      .test(
        'isProjectExist',
        `${singularProjectsModuleName} name already exists`,
        function (value) {
          if (!value || !projects) return true;
          const isProjectExists =
            !!projects?.projects?.find(
              (_project) => _project?.name?.toLowerCase() === value?.trim()?.toLowerCase(),
            ) && project?.name?.trim()?.toLowerCase() !== value?.trim()?.toLowerCase();
          return !isProjectExists;
        },
      ),
    // zip: Yup.string()
    //   .matches(commonRegex('number')?.regex, commonRegex('number')?.message)
    //   .nullable()
    //   .notRequired(),
  });

function useProjectsForm({
  project,
  setOpen,
  createProjectFromAddPostModal,
}: {
  project?: ProjectType;
  setOpen: Dispatch<SetStateAction<boolean>>;
  createProjectFromAddPostModal?: { articleFormState: PostFormData; projectName: string };
}) {
  const { singularProjectsModuleName } = useBuildWiseModules();
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const { companyId } = useUserData();
  const navigate = useNavigate();
  const {
    data: projects,
    isLoading: projectsLoading,
    refetch: refetchProjects,
  } = useSearchItemQuery<ProjectsSearchResponseType>(
    {
      searchType: 'projects',
      operator: 'or',
      filterBy: {
        filters: [
          {
            filterType: 'name',
            filterValue: 'ALL',
          },
        ],
      },
    },
    true,
  );
  const { activeUniqueEntries } = useConfigQuery({
    ...(companyId && { companyId }),
    configName: CONFIGS.PROJECT_INPUT_FIELDS.configName,
  });

  const customInputFields = useMemo(() => {
    const resultantMap = new Map(
      [...DEFAULT_STANDARD_PROJECT_FIELDS, ...activeUniqueEntries].map((obj) => [
        obj.entryCode,
        obj,
      ]),
    );
    return [...resultantMap.values()];
  }, [activeUniqueEntries]);
  const getDefaultValues = () => {
    return {
      ...getCustomFieldsDefaultValues({ data: project, fields: customInputFields }),
    };
  };
  const projectForm = useForm<ModifyProjectFormSchema>({
    resolver: yupResolver(
      editProjectSchema(customInputFields, singularProjectsModuleName, projects, project),
    ),
    defaultValues: getDefaultValues(),
  });
  const {
    formState: { isSubmitting, isDirty, isValid },
    setValue,
    handleSubmit,
    reset,
  } = projectForm;

  const isSaveDisabled = useMemo(
    () => projectsLoading || isSubmitting,
    [projectsLoading, isSubmitting],
  );

  useEffect(() => {
    if (customInputFields.length > 0) {
      reset(getDefaultValues(), {
        keepDirtyValues: true,
        keepErrors: true,
        keepTouched: true,
      });
    }
  }, [customInputFields]);

  useEffect(() => {
    if (createProjectFromAddPostModal?.projectName) {
      setTimeout(() => {
        setValue('name', createProjectFromAddPostModal?.projectName, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }, 100);
    }
  }, [createProjectFromAddPostModal?.projectName]);

  const closeModal = useCallback(() => {
    reset();
    setOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setOpenConfirmation(true);
    } else {
      closeModal();
    }
  }, [isDirty, closeModal]);

  const onSubmit = handleSubmit(
    async (values: ModifyProjectFormSchema) => {
      try {
        const payload = {
          ...project,
          companyId,
          ...values,
        };
        if (project) {
          await ProjectsAPI.editProject(companyId, project?.id, payload);
          toast.success(`${singularProjectsModuleName} Updated Successfully`);
          updateProjectDetailsCache(companyId, payload as ProjectType);
          updateProjectsCache(companyId, payload as ProjectType, 'edit');
          refetchProjects();
        } else {
          const { data } = await ProjectsAPI.addProject(companyId, payload);
          toast.success(`${singularProjectsModuleName} Created Successfully`);
          updateProjectsCache(companyId, data);
          if (createProjectFromAddPostModal?.articleFormState) {
            navigate(HOME, {
              state: {
                articleFormState: {
                  ...createProjectFromAddPostModal?.articleFormState,
                  projectId: data?.id,
                },
              },
            });
          }
        }
      } catch (e) {
        toast.error(catchErrorMessage(e));
      } finally {
        closeModal();
      }
    },
    () => {
      setOpenConfirmation(false);
    },
  );

  return {
    ...projectForm,
    customInputFields,
    isSubmitting,
    projectForm,
    isDirty,
    isSaveDisabled,
    singularProjectsModuleName,
    openConfirmation,
    setOpenConfirmation,
    closeModal,
    onSubmit,
    handleClose,
  };
}

export default useProjectsForm;
